import {
  ClientAwardDto,
  ClientAwardType,
} from "@growth-machine-llc/stridist-api-client";
import { convertWeight, formatWeight } from "../../utils/units";
import { Units } from "../../constants";
import mostChattyComments from "./award-descriptions/mostChatty.json";
import mostKgLiftedComments from "./award-descriptions/mostKgLifted.json";

type AwardMetadata = {
  title: string;
  message?: string;
  comment?: string;
  cta: string;
};

export type ClientAward = {
  type: string;
  title: string;
  clientName: string;
  displayName: string;
  clientAvatar: string;
  metric: string;
  message?: string;
  cta: string;
  breakdown?: string;
  beforeWeight?: string;
  afterWeight?: string;
};

export const CLIENT_AWARD_METADATA: Record<string, AwardMetadata> = {
  mostChatty: {
    title: "Most Chatty Client",
    message:
      "Sometimes the most chatty clients are the best, but they also take the most energy.",
    cta: "Learn More about Team Account",
  },
  mostKgLifted: {
    title: "Strongest Lifter",
    message:
      "{displayName} put in serious work in 2025! Want to see exactly which muscle groups powered these massive numbers?",
    cta: "View Exercise History",
  },
  mostCompletions: {
    title: "Most Dedicated",
    comment: "{displayName} crushed it in 2025!",
    message:
      "{displayName} stayed on track all year because of the structure you provided. To keep this momentum going with all your clients, use your Program Library to organize your signature plans.",
    cta: "View Program Library",
  },
  bestNutrition: {
    title: "Nutrition Champion",
    comment: "{displayName} nailed their nutrition goals in 2025!",
    cta: "View Nutrition Page",
  },
  bestTransformation: {
    title: "Most Impressive Transformation",
    comment: "{displayName}'s transformation is incredible!",
    message:
      "Transformations like this are your best marketing. Did you know you can replace Stridist logo with yours whenever a client shares their photos?",
    cta: "Upgrade to CBA Package",
  },
} as const;

export const AWARD_TYPE_TO_META_KEY: Record<
  ClientAwardType,
  keyof typeof CLIENT_AWARD_METADATA
> = {
  [ClientAwardType.MostChatty]: "mostChatty",
  [ClientAwardType.StrongestLifter]: "mostKgLifted",
  [ClientAwardType.MostDedicated]: "mostCompletions",
  [ClientAwardType.BestNutrition]: "bestNutrition",
  [ClientAwardType.BestTransformation]: "bestTransformation",
};

export const getNutritionComment = (percent: number, displayName: string) => {
  if (percent >= 100) {
    return `${displayName} consistently hit their nutrition targets with near-perfect accuracy!`;
  }
  if (percent >= 75) {
    return `${displayName} stayed very consistent with their nutrition goals most of the year.`;
  }
  if (percent >= 50) {
    return `${displayName} made solid progress toward their nutrition targets.`;
  }
  return `${displayName} started building healthier nutrition habits this year.`;
};

const getMostChattyComment = (messagesCount: number): string | null => {
  const entry = mostChattyComments.find((item) => {
    if (item.maxMessages === null) {
      // For the highest tier (no max limit)
      return messagesCount >= item.minMessages;
    }
    return (
      messagesCount >= item.minMessages && messagesCount <= item.maxMessages
    );
  });

  return entry?.comment || null;
};

const getStrongestLifterComment = (kgLifted: number): string | null => {
  const entry = mostKgLiftedComments.find((item) => {
    if (item.maxKg === null) {
      // For the highest tier (no max limit)
      return kgLifted >= item.minKg;
    }
    return kgLifted >= item.minKg && kgLifted <= item.maxKg;
  });

  return entry?.comment || null;
};

export const groupNutritionTypes = (
  types: Array<{ type: string; count: number }>,
) => {
  const result = {
    "All Days": 0,
    "Training & Rest Days": 0,
    "Weekdays & Weekends": 0,
  };

  types.forEach((item) => {
    switch (item.type) {
      case "ALL_DAYS":
        result["All Days"] += item.count;
        break;
      case "OFF_DAYS":
      case "TRAINING_DAYS":
        result["Training & Rest Days"] += item.count;
        break;
      case "WEEKDAYS":
      case "WEEKENDS":
        result["Weekdays & Weekends"] += item.count;
        break;
      default:
        break;
    }
  });

  return Object.entries(result)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({ type, count }));
};

export const normalizeAwardData = (
  award: ClientAwardDto,
  units: Units,
  breakdownByClient?: Record<
    string,
    { Exercises: number; Lessons: number; CheckIns: number }
  >,
) => {
  const metaKey =
    AWARD_TYPE_TO_META_KEY[award.type ?? ClientAwardType.MostChatty];
  const metadata = CLIENT_AWARD_METADATA[metaKey];

  let metric = "";
  let breakdown = award.breakdown;
  const weightLabel = units === Units.METRIC ? "kg" : "lbs";

  switch (award.type) {
    case ClientAwardType.MostChatty:
      metric = `${award.metric ?? 0} messages`;
      break;
    case ClientAwardType.StrongestLifter:
      metric = `${(Math.round(convertWeight(award.metric, "kg", units)) ?? 0).toLocaleString()} ${weightLabel} lifted`;
      break;
    case ClientAwardType.MostDedicated: {
      const workouts = award.workouts ?? 0;
      const les = award.lessons ?? 0;
      const ch = award.checkIns ?? 0;

      const total = workouts + les + ch;
      metric = `${total} completed activities`;

      breakdown = `${workouts} workouts • ${les} lessons • ${ch} check-ins`;
      break;
    }
    case ClientAwardType.BestNutrition:
      metric = `${award.metric?.toFixed(0) ?? 0}% perfect score`;
      break;
    case ClientAwardType.BestTransformation:
      metric = formatWeight(
        convertWeight(award.metric ?? 0, "kg", units),
        units,
        true,
      );
      break;
  }

  const nutritionPercent =
    award.type === ClientAwardType.BestNutrition &&
    typeof award.metric === "number"
      ? award.metric
      : 0;

  return {
    type: metaKey,
    title: metadata.title,
    clientName: award.clientName,
    displayName: award.displayName ?? award.clientName,
    clientAvatar: award.clientAvatar ?? "/placeholder.svg",
    metric,
    message: metadata.message?.replace("{displayName}", award.displayName),
    comment:
      award.type === ClientAwardType.BestNutrition
        ? getNutritionComment(nutritionPercent, award.displayName)
        : award.type === ClientAwardType.MostChatty
          ? getMostChattyComment(Number(award.metric ?? 0))
          : award.type === ClientAwardType.StrongestLifter
            ? getStrongestLifterComment(Number(award.metric ?? 0))
            : metadata.comment?.replace("{displayName}", award.displayName),
    breakdown,
    cta: metadata.cta,
    beforeWeight: award.beforeWeight
      ? formatWeight(
          convertWeight(award.beforeWeight, "kg", units),
          units,
          true,
        )
      : undefined,
    afterWeight: award.afterWeight
      ? formatWeight(convertWeight(award.afterWeight, "kg", units), units, true)
      : undefined,
  };
};

export const getAwardGradient = (type: string) => {
  switch (type) {
    case "mostChatty":
      return "linear-gradient(135deg, #ec4899 0%, #06b6d4 50%)";
    case "mostKgLifted":
      return "linear-gradient(135deg, #f59e0b 0%, #ef4444 50%)";
    case "mostCompletions":
      return "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%)";
    case "bestNutrition":
      return "linear-gradient(135deg, #10b981 0%, #06b6d4 50%)";
    case "bestTransformation":
      return "linear-gradient(135deg, #ec4899 0%, #f59e0b 50%)";
    default:
      return "linear-gradient(135deg, #ec4899 0%, #06b6d4 50%)";
  }
};
